import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import dayjs from 'dayjs';
import { useAtomValue } from 'jotai/index';
import { isNonNullish } from 'remeda';
import { SettingsContainer } from 'wbs-components-lib';

import { Box, Button, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

import { ServerDataGrid } from '@shared/components';
import { TelegramChatStatusesEnum } from '@shared/constants';
import { useMasterKeyPopover, useModal, useUserPopover } from '@shared/hooks';
import { tableStyles } from '@shared/styles';
import { TelegramChatModel } from '@shared/types';
import { getFilterOperators } from '@shared/utils';

import { useUpdateTelegramStatusMutation } from '@services';

import {
  AppDispatch,
  getProcessingTelegramChatSelector,
  setAssignedTelegramChat,
  setProcessingTelegramChat,
  updateInitialTelegramChat,
} from '@store';

import { TelegramChatSettingsTableTypesEnum } from '../constants';
import { TelegramChatFinishSettingsModal } from '../TelegramChatFinishSettingsModal';
import { TelegramChatSettingsModal } from '../TelegramChatSettingsModal';

import { TELEGRAM_CHAT_PAGE_LIMIT } from './constants';
import { TelegramChatSettingsTableActions } from './TelegramChatSettingsTableActions';
import { TelegramChatSettingsTableProps } from './types';
import { getTelegramChatRowClassName } from './utils';

import styles from './TelegramChatSettingsTable.module.css';

import { userAtom } from 'v2/atoms/userAtom';

const { headerContainer, notificationDot, actionsContainer } = styles;

const { tableContainer } = tableStyles;

export const TelegramChatSettingsTable: FC<TelegramChatSettingsTableProps> = ({
  setListAction,
  setPageAction,
  setTotalCountAction,
  pageSelector,
  totalCountSelector,
  listSelector,
  useLazyListQuery,
  updateTelegramChat,
  telegramChatTableType,
  handleChatModalClose,
  handleChatModalOpen,
  isChatModelOpened,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [isFinishTelegramChatModalOpened, openFinishTelegramChatModal, closeFinishTelegramChatModal] = useModal();

  const processingTelegramChat = useSelector(getProcessingTelegramChatSelector);

  const handleOpenFinishTelegramChatModal = (telegramChat: TelegramChatModel) => {
    dispatch(setProcessingTelegramChat(telegramChat));

    openFinishTelegramChatModal();
  };

  const user = useAtomValue(userAtom);

  const [sort, setSort] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);
  const [filter, setFilter] = useState<Record<string, string>>(
    isNonNullish(user) && telegramChatTableType === TelegramChatSettingsTableTypesEnum.CARD_ASSIGNED
      ? { moderatorId: user?.id }
      : {}
  );

  const { handleOpenPopover: handleOpenUserContent } = useUserPopover();
  const { handleOpenPopover: handleOpenMasterKeyPopover } = useMasterKeyPopover();

  const [processTelegramChat] = useUpdateTelegramStatusMutation();

  const handleTakeTelegramChat = async (telegramChatId: string) => {
    const updatedTelegramChat = await processTelegramChat({
      telegramChatId,
      payload: { status: TelegramChatStatusesEnum.PROCESSED },
    }).unwrap();

    if ([TelegramChatSettingsTableTypesEnum.CARD_ASSIGNED].includes(telegramChatTableType)) {
      dispatch(updateTelegramChat(updatedTelegramChat));
    } else {
      dispatch(updateInitialTelegramChat(updatedTelegramChat));
      dispatch(setAssignedTelegramChat(updatedTelegramChat));
    }
  };

  const userColumns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'Created At',
      minWidth: 150,
      flex: 1,
      filterOperators: getFilterOperators(['contains']),
      hideable: false,
      filterable: false,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, TelegramChatModel>) => {
        const {
          row: { createdAt },
        } = params;

        return dayjs(createdAt).format('DD.MM.YYYY HH:mm');
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      minWidth: 150,
      flex: 1,
      filterOperators: getFilterOperators(['contains']),
      hideable: false,
      filterable: false,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, TelegramChatModel>) => {
        const {
          row: { updatedAt },
        } = params;

        return dayjs(updatedAt).format('DD.MM.YYYY HH:mm');
      },
    },
    {
      field: 'userNickname',
      headerName: 'User Nickname',
      minWidth: 150,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      filterable: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, TelegramChatModel>) => {
        const {
          row: { userId, userNickname },
        } = params;

        return (
          <Button id={userId} onClick={handleOpenUserContent}>
            {userNickname}
          </Button>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 75,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      filterable: true,
      sortable: true,
    },
    {
      field: 'comment',
      headerName: 'Comment',
      minWidth: 100,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      filterable: true,
      sortable: true,
    },
    {
      field: 'moderatorId',
      headerName: 'Moderator Id',
      minWidth: 150,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      filterable: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, TelegramChatModel>) => {
        const {
          row: { moderatorId },
        } = params;

        return (
          moderatorId && (
            <Button id={moderatorId} onClick={handleOpenMasterKeyPopover}>
              {moderatorId}
            </Button>
          )
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 200,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, TelegramChatModel>) => {
        const { row } = params;

        const { id, status, isChatMessagesRead } = row;

        return (
          <Box className={actionsContainer}>
            <TelegramChatSettingsTableActions
              isChatUnread={!isChatMessagesRead}
              status={status}
              handleOpenFinishTelegramChatModal={() => handleOpenFinishTelegramChatModal(row)}
              handleTakeTelegramChat={() => handleTakeTelegramChat(id)}
              handleOpenTelegramChatModal={() => handleChatModalOpen(row)}
            />
            {!isChatMessagesRead && <Typography component="span" className={notificationDot} />}
          </Box>
        );
      },
    },
  ];

  const getTableName = (tableType: TelegramChatSettingsTableTypesEnum) => {
    switch (tableType) {
      case TelegramChatSettingsTableTypesEnum.CARD_ASSIGNED: {
        return 'My assigned';
      }
      case TelegramChatSettingsTableTypesEnum.ALL: {
        return 'All';
      }
    }
  };

  return (
    <>
      <SettingsContainer>
        <Box className={headerContainer}>
          <Typography margin="10px" fontSize="20px" fontWeight={600}>
            {getTableName(telegramChatTableType)} TelegramChats
          </Typography>
        </Box>
        <Box className={tableContainer}>
          <ServerDataGrid<TelegramChatModel>
            initialState={{
              columns: {
                columnVisibilityModel: {
                  moderatorId: telegramChatTableType === TelegramChatSettingsTableTypesEnum.ALL,
                },
              },
            }}
            columns={userColumns}
            pageSelector={pageSelector}
            setPageAction={setPageAction}
            totalCountSelector={totalCountSelector}
            setTotalCountAction={setTotalCountAction}
            listSelector={listSelector}
            setListAction={setListAction}
            limit={TELEGRAM_CHAT_PAGE_LIMIT}
            filter={filter}
            setFilter={setFilter}
            sort={sort}
            setSort={setSort}
            useLazyListQuery={useLazyListQuery}
            getRowClassName={getTelegramChatRowClassName}
          />
        </Box>
      </SettingsContainer>
      {processingTelegramChat && (
        <>
          <TelegramChatFinishSettingsModal
            isOpened={isFinishTelegramChatModalOpened}
            handleClose={closeFinishTelegramChatModal}
            telegramChat={processingTelegramChat}
            updateTelegramChat={updateTelegramChat}
          />
          <TelegramChatSettingsModal
            isOpened={isChatModelOpened}
            handleClose={handleChatModalClose}
            telegramChat={processingTelegramChat}
          />
        </>
      )}
    </>
  );
};
